import { ConfigBlock, FieldConfig } from '../Components/Page/PageTypes';
import { DealRecieverDetailMap, MasterData } from './useDADataTransformations';
import { convertToFirstLetterCapital, isEmpty } from '../../../utils/utils';
import { RANGE_REQUIREMENT } from '../../ManageDealDrawer/DealDrawerConstants';

/**
 * The result of validating the form data against the config blocks.
 */
interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

/**
 * Validate the data in formData against a list of config blocks.
 *
 * @param configBlocks - An array of configuration blocks to validate against
 * @param formData - The MasterData from your hook (products, dealTerms, dealReceiverDetailMap, etc.)
 */
export function validateFormData(configBlocks: ConfigBlock[], formData: MasterData): ValidationResult {
    const errors: string[] = [];

    /**
     * A helper function to get the data segment for a particular sectionKey.
     * If `sectionKey` is undefined or null, we treat `formData` as the direct container.
     */
    function getSectionData(sectionKey: string | null | undefined): any {
        if (!sectionKey) {
            // If there's no sectionKey, just return the top-level formData
            return formData;
        }

        // For a typical scenario:
        // If sectionKey === 'dealTerms', then we use formData.dealTerms
        // If sectionKey === 'products', etc., adjust to your structure
        // Change the logic below if you store certain sectionKeys in different places.
        return (formData as any)[sectionKey];
    }

    /**
     * Validate a single field against the form data.
     *
     * @param fieldKey - The key in the config
     * @param fieldConfig - The metadata for the field
     * @param sectionData - The subset of formData that corresponds to the configBlock's sectionKey
     * @param prefixForErrors - A prefix to help clarify which section/investor we’re validating (optional)
     */
    function validateField(fieldKey: string, fieldConfig: FieldConfig, sectionData: any, prefixForErrors: string = '') {
        if (fieldConfig.type === 'range') {
            const capitalizedKey = convertToFirstLetterCapital(fieldKey ?? '');
            const minKey = `minimum${capitalizedKey}`;
            const maxKey = `maximum${capitalizedKey}`;

            const minVal = sectionData?.[minKey];
            const maxVal = sectionData?.[maxKey];
            if (!isEmpty(minVal) && !isEmpty(maxVal)) {
                if (Number(minVal) > Number(maxVal)) {
                    errors.push(`${prefixForErrors}Minimum value of "${fieldKey}" should be less than or equal to maximum value.`);
                }
            }
        }

        // If the field is required, check if data is present and valid
        if (fieldConfig.isRequired) {
            if (fieldConfig.type === 'range') {
                // For a "range", we might store the min/max as "minimum<FieldKey>" / "maximum<FieldKey>"
                const capitalizedKey = convertToFirstLetterCapital(fieldKey ?? '');
                const minKey = `minimum${capitalizedKey}`;
                const maxKey = `maximum${capitalizedKey}`;

                const minVal = sectionData?.[minKey];
                const maxVal = sectionData?.[maxKey];

                if (!minVal || !maxVal) {
                    errors.push(`${prefixForErrors}Range field "${fieldKey}" is required but missing minimum or maximum value.`);
                }
                // Additional numeric checks can go here (e.g., minVal <= maxVal).
            } else {
                const value = sectionData?.[fieldKey];
                // For arrays (multi-select), you might check .length === 0
                // For strings or numbers, you might check null/undefined or empty string
                if (value == null || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
                    errors.push(`${prefixForErrors}Field "${fieldKey}" is required but is missing or empty.`);
                }
            }
        }
    }

    // Main loop over the configBlocks
    configBlocks?.forEach((configBlock) => {
        const sectionData = getSectionData(configBlock.sectionKey);

        // For each field in the configBlock, validate
        Object.entries(configBlock.config).forEach(([fieldKey, fieldConfig]) => {
            validateField(fieldKey, fieldConfig, sectionData);
        });
    });

    return {
        isValid: errors.length === 0,
        errors,
    };
}

/**
 * Validates a single form section against the form data.
 *
 * @param section - The section configuration to validate
 * @param formData - The complete form data
 * @returns ValidationResult with isValid flag and any errors
 */
export function validateFormSection(section: any, formData: Record<string, any>): ValidationResult {
    const errors: string[] = [];

    if (!section || !section.config) {
        return { isValid: true, errors: [] };
    }

    // Check each field in the section configuration
    Object.entries(section.config).forEach(([key, fieldConfig]: [string, any]) => {
        const formKey = section.sectionKey ? `${section.sectionKey}.${key}` : key;

        // Handle range type fields with rangeRequirement
        if (fieldConfig.type === 'range') {
            const capitalizedKey = convertToFirstLetterCapital(key);
            const minKey = section.sectionKey ? `${section.sectionKey}.minimum${capitalizedKey}` : `minimum${capitalizedKey}`;
            const maxKey = section.sectionKey ? `${section.sectionKey}.maximum${capitalizedKey}` : `maximum${capitalizedKey}`;

            // First check if values are valid when both exist
            if (!isEmpty(formData[minKey]) && !isEmpty(formData[maxKey])) {
                if (Number(formData[minKey]) > Number(formData[maxKey])) {
                    errors.push('Required');
                }
            }

            if (fieldConfig.isRequired) {
                const rangeRequirement = fieldConfig.rangeRequirement || RANGE_REQUIREMENT.BOTH;

                if (rangeRequirement === RANGE_REQUIREMENT.MIN && isEmpty(formData[minKey])) {
                    errors.push('Required');
                } else if (rangeRequirement === RANGE_REQUIREMENT.MAX && isEmpty(formData[maxKey])) {
                    errors.push('Required');
                } else if (rangeRequirement === RANGE_REQUIREMENT.BOTH && (isEmpty(formData[minKey]) || isEmpty(formData[maxKey]))) {
                    errors.push('Required');
                }
            }
        } else if (fieldConfig.importance && fieldConfig.isRequired) {
            const importanceKey = section.sectionKey
                ? `${section.sectionKey}.importance${convertToFirstLetterCapital(key)}`
                : `importance${convertToFirstLetterCapital(key)}`;

            if (!isEmpty(formData[formKey]) && isEmpty(formData[importanceKey])) {
                errors.push('Required');
            }
        } else if (fieldConfig.isRequired) {
            const value = formData[formKey];
            if (value == null || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
                errors.push('Required');
            }
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
    };
}

export const validateDealRecievers = (lenders: DealRecieverDetailMap) => {
    const errors: string[] = [];
    for (const [key, value] of lenders) {
        if (value.productEnums.length === 0) {
            errors.push(`Product is required for ${key}`);
        }
        if (Object.keys(value.dealTerms).length === 0) {
            errors.push(`Deal terms are required for ${key}`);
        }
        if (value?.dealTerms?.['loanTypes'].length === 0) {
            errors.push(`Loan types are required for ${key}`);
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
};
